import React, { useState, useEffect } from 'react';
import { InlineSpinner } from '../../common/loading';
import { SectionSuspenseSkeleton } from '../../common/loading';

import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Paper,
  Container,
  Alert,
  alpha,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { publicFeedbackApi, PublicFeedbackSubmit, PublicFeedbackFormResponse } from '../../../services/api';

// Searce brand colors - Official palette
const SEARCE_COLORS = {
  primary: '#0064FF',       // Searce Blue (B5)
  primaryDark: '#0051CB',   // B4
  secondary: '#734BFF',     // Purple - Official secondary
  turquoise: '#00C8FF',     // Turquoise accent
  white: '#ffffff',
  textPrimary: '#000000',   // Pure Black
  textSecondary: '#232324', // Grey
  border: '#E6E6E6',        // Lightest Grey
  cardBackground: '#ffffff',
  error: '#EA4255',
  success: '#66D89B',
  warning: '#F4B643',
  background: '#F2F7FF',    // B11 - lightest blue
};

// Uniform font family
const FONT_FAMILY = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif';

// Form text field styling
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: SEARCE_COLORS.cardBackground,
    fontFamily: FONT_FAMILY,
    fontSize: '0.875rem',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: SEARCE_COLORS.primary,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: SEARCE_COLORS.primary,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: SEARCE_COLORS.primary,
  },
  '& .MuiInputBase-input': {
    fontFamily: FONT_FAMILY,
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-input::placeholder': {
    fontFamily: FONT_FAMILY,
    fontSize: '0.875rem',
    opacity: 0.6,
  },
};

// Common label styling
const labelSx = {
  fontFamily: FONT_FAMILY,
  fontWeight: 500,
  fontSize: '0.875rem',
  color: SEARCE_COLORS.textPrimary,
  mb: 1,
};

// Rating scale options
const SATISFACTION_OPTIONS = [
  'Highly Satisfied',
  'Satisfied', 
  'Neutral',
  'Dissatisfied',
  'Highly Dissatisfied',
];

const QUALITY_RATINGS = ['Poor', 'Not Good', 'Satisfactory', 'Great', 'Awesome'];

const DELIVERY_FACTORS = [
  'Completion of Deliverables',
  'Adherence to Timeline',
  'Project Closure and Handover',
];

const TEAM_FACTORS = [
  'Technical Expertise',
  'Support Provided',
  'Responsiveness',
];

interface DeliveryRatings {
  completion_of_deliverables: string;
  adherence_to_timeline: string;
  project_closure_and_handover: string;
}

interface TeamRatings {
  technical_expertise: string;
  support_provided: string;
  responsiveness: string;
}

interface FormData {
  customer_email: string;
  customer_name: string;
  overall_satisfaction: string;
  delivery_ratings: DeliveryRatings;
  team_ratings: TeamRatings;
  specific_contributions: string;
  areas_for_improvement: string;
}

const initialFormData: FormData = {
  customer_email: '',
  customer_name: '',
  overall_satisfaction: '',
  delivery_ratings: {
    completion_of_deliverables: '',
    adherence_to_timeline: '',
    project_closure_and_handover: '',
  },
  team_ratings: {
    technical_expertise: '',
    support_provided: '',
    responsiveness: '',
  },
  specific_contributions: '',
  areas_for_improvement: '',
};

type PageStatus = 'loading' | 'valid' | 'invalid' | 'expired' | 'used' | 'submitted' | 'error';

export default function PublicCustomerFeedbackPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<PageStatus>('loading');
  const [formInfo, setFormInfo] = useState<PublicFeedbackFormResponse | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('invalid');
        setErrorMessage('Invalid feedback link');
        return;
      }

      try {
        const response = await publicFeedbackApi.getForm(token);
        setFormInfo(response);

        if (!response.is_valid) {
          if (response.message?.includes('already been used')) {
            setStatus('used');
          } else if (response.message?.includes('expired')) {
            setStatus('expired');
          } else {
            setStatus('invalid');
          }
          setErrorMessage(response.message || 'Invalid feedback link');
        } else {
          setStatus('valid');
          // Pre-fill account name if available
          if (response.company_name) {
            setFormData(prev => ({ ...prev, account_name: response.company_name || '' }));
          }
        }
      } catch (error: any) {
        console.error('Error validating token:', error);
        setStatus('error');
        setErrorMessage('Failed to validate feedback link. Please try again.');
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDeliveryRatingChange = (factor: keyof DeliveryRatings, value: string) => {
    setFormData(prev => ({
      ...prev,
      delivery_ratings: { ...prev.delivery_ratings, [factor]: value },
    }));
  };

  const handleTeamRatingChange = (factor: keyof TeamRatings, value: string) => {
    setFormData(prev => ({
      ...prev,
      team_ratings: { ...prev.team_ratings, [factor]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) return;

    // Validate required fields
    if (!formData.customer_email || !formData.customer_name || !formData.overall_satisfaction) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    // Validate delivery ratings
    const deliveryRatingsComplete = Object.values(formData.delivery_ratings).every(v => v !== '');
    if (!deliveryRatingsComplete) {
      setErrorMessage('Please rate all delivery quality factors');
      return;
    }

    // Validate team ratings
    const teamRatingsComplete = Object.values(formData.team_ratings).every(v => v !== '');
    if (!teamRatingsComplete) {
      setErrorMessage('Please rate all team quality factors');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const submitData: PublicFeedbackSubmit = {
        // Customer Information
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        account_name: formInfo?.company_name || '',
        // Overall Satisfaction
        overall_satisfaction: formData.overall_satisfaction,
        // Delivery Quality Ratings (flat fields)
        delivery_completion_of_deliverables: formData.delivery_ratings.completion_of_deliverables,
        delivery_adherence_to_timeline: formData.delivery_ratings.adherence_to_timeline,
        delivery_project_closure_handover: formData.delivery_ratings.project_closure_and_handover,
        // Team Quality Ratings (flat fields)
        team_technical_expertise: formData.team_ratings.technical_expertise,
        team_support_provided: formData.team_ratings.support_provided,
        team_responsiveness: formData.team_ratings.responsiveness,
        // Open-ended Feedback
        specific_contributions: formData.specific_contributions || undefined,
        areas_for_improvement: formData.areas_for_improvement || undefined,
      };

      const response = await publicFeedbackApi.submit(token, submitData);
      
      if (response.success) {
        setStatus('submitted');
      } else {
        setErrorMessage(response.message || 'Failed to submit feedback');
      }
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${SEARCE_COLORS.background} 0%, ${alpha(SEARCE_COLORS.primary, 0.05)} 100%)`,
        }}
      >
        <Paper elevation={0} sx={{ p: 3, maxWidth: 520, width: '100%', borderRadius: 2 }}>
          <SectionSuspenseSkeleton message="Validating your feedback link…" minHeight={200} py={2} />
        </Paper>
      </Box>
    );
  }

  // Success state
  if (status === 'submitted') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${SEARCE_COLORS.background} 0%, ${alpha(SEARCE_COLORS.success, 0.05)} 100%)`,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px solid ${SEARCE_COLORS.border}`,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: SEARCE_COLORS.success, mb: 3 }} />
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: '1.5rem', color: SEARCE_COLORS.primary, mb: 2 }}>
              Thank You!
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: SEARCE_COLORS.textSecondary, mb: 3 }}>
              Your feedback has been submitted successfully. We greatly appreciate you taking the time to share your experience with us.
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem', color: SEARCE_COLORS.textSecondary }}>
              You can close this window now.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Error states (invalid, expired, used)
  if (status === 'invalid' || status === 'expired' || status === 'used' || status === 'error') {
    const getErrorContent = () => {
      switch (status) {
        case 'expired':
          return {
            icon: <AccessTimeIcon sx={{ fontSize: 80, color: SEARCE_COLORS.warning, mb: 3 }} />,
            title: 'Link Expired',
            message: 'This feedback link has expired. Please contact the person who sent you this link to request a new one.',
          };
        case 'used':
          return {
            icon: <CheckCircleIcon sx={{ fontSize: 80, color: SEARCE_COLORS.primary, mb: 3 }} />,
            title: 'Already Submitted',
            message: 'Feedback has already been submitted using this link. Thank you for your response!',
          };
        default:
          return {
            icon: <ErrorIcon sx={{ fontSize: 80, color: SEARCE_COLORS.error, mb: 3 }} />,
            title: 'Invalid Link',
            message: errorMessage || 'This feedback link is invalid or no longer available.',
          };
      }
    };

    const errorContent = getErrorContent();

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${SEARCE_COLORS.background} 0%, ${alpha(SEARCE_COLORS.primary, 0.05)} 100%)`,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px solid ${SEARCE_COLORS.border}`,
            }}
          >
            {errorContent.icon}
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: '1.5rem', color: SEARCE_COLORS.primary, mb: 2 }}>
              {errorContent.title}
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: SEARCE_COLORS.textSecondary }}>
              {errorContent.message}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Valid - show form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 3,
        background: `linear-gradient(135deg, ${SEARCE_COLORS.background} 0%, ${alpha(SEARCE_COLORS.primary, 0.05)} 100%)`,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: '2rem',
              color: SEARCE_COLORS.primary,
              mb: 0.5,
            }}
          >
            Submit Customer Sign-Off
          </Typography>
          {formInfo?.project_title && (
            <Typography sx={{ 
              fontFamily: FONT_FAMILY, 
              fontSize: '1rem', 
              color: SEARCE_COLORS.textSecondary, 
              fontWeight: 500,
              mb: 0.5,
            }}>
              {formInfo.project_title}
            </Typography>
          )}
          <Typography sx={{ 
            fontFamily: FONT_FAMILY, 
            fontSize: '0.875rem', 
            color: SEARCE_COLORS.textSecondary,
          }}>
            We value your feedback. Please take a moment to share your experience.
          </Typography>
        </Box>

        {/* Form Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${SEARCE_COLORS.border}`,
            overflow: 'hidden',
          }}
        >
          {/* Accent bar */}
          <Box
            sx={{
              height: 4,
              background: `linear-gradient(90deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.secondary} 100%)`,
            }}
          />

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Project Code/Opportunity ID - Auto-filled (disabled) */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}`, backgroundColor: alpha(SEARCE_COLORS.border, 0.2) }}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.8rem', color: SEARCE_COLORS.textSecondary, mb: 0.5 }}>
                    Project Code
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.875rem', color: SEARCE_COLORS.textPrimary }}>
                    {formInfo?.project_code || '-'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Company/Account Name - Auto-filled (disabled) */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}`, backgroundColor: alpha(SEARCE_COLORS.border, 0.2) }}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.8rem', color: SEARCE_COLORS.textSecondary, mb: 0.5 }}>
                    Account Name
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.875rem', color: SEARCE_COLORS.textPrimary }}>
                    {formInfo?.company_name || '-'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Email Address */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    Email Address <span style={{ color: SEARCE_COLORS.error }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    required
                    value={formData.customer_email}
                    onChange={(e) => handleInputChange('customer_email', e.target.value)}
                    placeholder="your.email@company.com"
                    sx={textFieldSx}
                  />
                </Paper>
              </Grid>

              {/* Full Name of customer */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    Full Name <span style={{ color: SEARCE_COLORS.error }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    value={formData.customer_name}
                    onChange={(e) => handleInputChange('customer_name', e.target.value)}
                    placeholder="Your Name"
                    sx={textFieldSx}
                  />
                </Paper>
              </Grid>

              {/* Overall Satisfaction */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    How satisfied are you with the overall experience with Searce? <span style={{ color: SEARCE_COLORS.error }}>*</span>
                  </Typography>
                  <RadioGroup
                    row
                    value={formData.overall_satisfaction}
                    onChange={(e) => handleInputChange('overall_satisfaction', e.target.value)}
                  >
                    {SATISFACTION_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio size="small" sx={{ '&.Mui-checked': { color: SEARCE_COLORS.primary } }} />}
                        label={<Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem' }}>{option}</Typography>}
                        sx={{ mr: 2 }}
                      />
                    ))}
                  </RadioGroup>
                </Paper>
              </Grid>

              {/* Quality of Delivery Ratings */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    How would you rate the quality of delivery? <span style={{ color: SEARCE_COLORS.error }}>*</span>
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1, fontFamily: FONT_FAMILY } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.75rem', minWidth: 140 }}></TableCell>
                          {QUALITY_RATINGS.map((rating) => (
                            <TableCell key={rating} align="center" sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.75rem', minWidth: 65, color: SEARCE_COLORS.textSecondary }}>
                              {rating}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {DELIVERY_FACTORS.map((factor) => {
                          const keyMap: { [key: string]: keyof DeliveryRatings } = {
                            'Completion of Deliverables': 'completion_of_deliverables',
                            'Adherence to Timeline': 'adherence_to_timeline',
                            'Project Closure and Handover': 'project_closure_and_handover',
                          };
                          const key = keyMap[factor];
                          return (
                            <TableRow key={factor}>
                              <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem', color: SEARCE_COLORS.textPrimary }}>{factor}</TableCell>
                              {QUALITY_RATINGS.map((rating) => (
                                <TableCell key={rating} align="center" sx={{ p: 0 }}>
                                  <Radio
                                    checked={formData.delivery_ratings[key] === rating}
                                    onChange={() => handleDeliveryRatingChange(key, rating)}
                                    size="small"
                                    sx={{ p: 0.5, '&.Mui-checked': { color: SEARCE_COLORS.primary } }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </Grid>

              {/* Quality of Team Ratings */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    How would you rate the quality of the team? <span style={{ color: SEARCE_COLORS.error }}>*</span>
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1, fontFamily: FONT_FAMILY } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.75rem', minWidth: 120 }}></TableCell>
                          {QUALITY_RATINGS.map((rating) => (
                            <TableCell key={rating} align="center" sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '0.75rem', minWidth: 65, color: SEARCE_COLORS.textSecondary }}>
                              {rating}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {TEAM_FACTORS.map((factor) => {
                          const keyMap: { [key: string]: keyof TeamRatings } = {
                            'Technical Expertise': 'technical_expertise',
                            'Support Provided': 'support_provided',
                            'Responsiveness': 'responsiveness',
                          };
                          const key = keyMap[factor];
                          return (
                            <TableRow key={factor}>
                              <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem', color: SEARCE_COLORS.textPrimary }}>{factor}</TableCell>
                              {QUALITY_RATINGS.map((rating) => (
                                <TableCell key={rating} align="center" sx={{ p: 0 }}>
                                  <Radio
                                    checked={formData.team_ratings[key] === rating}
                                    onChange={() => handleTeamRatingChange(key, rating)}
                                    size="small"
                                    sx={{ p: 0.5, '&.Mui-checked': { color: SEARCE_COLORS.primary } }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </Grid>

              {/* Specific contributions that delighted you */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    Specific contributions that delighted you
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={formData.specific_contributions}
                    onChange={(e) => handleInputChange('specific_contributions', e.target.value)}
                    placeholder="Your answer"
                    sx={textFieldSx}
                  />
                </Paper>
              </Grid>

              {/* Specific areas where we could have done better */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${SEARCE_COLORS.border}` }}>
                  <Typography sx={labelSx}>
                    Specific areas where we could have done better
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={formData.areas_for_improvement}
                    onChange={(e) => handleInputChange('areas_for_improvement', e.target.value)}
                    placeholder="Your answer"
                    sx={textFieldSx}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontFamily: FONT_FAMILY,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  backgroundColor: SEARCE_COLORS.primary,
                  boxShadow: '0 4px 14px rgba(0, 100, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: SEARCE_COLORS.primaryDark,
                    boxShadow: '0 6px 20px rgba(0, 100, 255, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.5),
                  },
                }}
              >
                {submitting ? (
                  <>
                    <InlineSpinner size={20} />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem', color: SEARCE_COLORS.textSecondary }}>
            Powered by Searce
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

